import { Controller } from '@hotwired/stimulus';

// Cycles a Landing::BrowserBeforeAfterCarouselComponent through its slides.
//
// The inner Effects::BeforeAfterEffectComponent runs one pure-CSS "before ->
// after" cycle on a loop. On its own, the tail of that cycle crossfades back to
// its *own* before frame; in a carousel that reads as an unwanted flick back to
// the previous site. So we keep the two image layers one slide out of phase:
//
//   during cycle c (= the CSS animation's currentIteration):
//     after layer  -> slide c            what the flick reveals, then holds
//     before layer -> slide c            while its cluttered page is on screen
//                  -> slide c + 1         once it has slipped off screen (progress >= HANDOFF)
//
// The effect's built-in after->before crossfade then runs straight from slide
// c's "after" to slide c+1's "before" — the return to slide c's before never
// shows. The URL bar flips a little later than the before-image swap (URL_HANDOFF
// vs HANDOFF): the swapped-in before frame stays masked by the opaque "after"
// until the crossfade begins (~90%), so flipping the URL at the swap would leave
// it naming the next site while slide c's screenshot is still fully on screen.
//
// State is computed each frame from the animation's own clock (getComputedTiming),
// so it needs no event edge-detection and self-corrects after the tab is
// backgrounded. Internals are reached by class name (.bae-card, .bae-page-* img,
// .bae-wordmark, .landing-browser-url-text); this controller only ever wraps those
// two components, so the coupling stays local. With prefers-reduced-motion the
// effect doesn't animate, so there's nothing to read and slide 0 holds — the
// accessible resting state.
//
// Only slide 0 is in the HTML; every other slide is fetched just before its cycle
// (see #preloadNext), a full cycle of lead time, so the panel never front-loads
// every image on render.
//
// The loop runs only while the carousel is on screen (IntersectionObserver): a closed modal is
// display:none, so its animation doesn't exist yet at connect() and a re-show creates a fresh one.

// The before layer clears the viewport at 88% of the cycle (bae-push-before), so
// it's safe to swap its image any time after that and before the next cycle.
const HANDOFF = 0.88;

// The "after" screenshot crossfades out over ~90%-97.5% (bae-push-after), so flip
// the URL mid-crossfade — late enough that it trails, never leads, the visible swap.
const URL_HANDOFF = 0.94;

export default class extends Controller {
  static values = { slides: Array };

  connect() {
    this.slides = this.slidesValue;
    if (this.slides.length <= 1) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return; // never animates -> hold slide 0

    this.card = this.element.querySelector('.bae-card');
    this.beforeImg = this.element.querySelector('.bae-page-before img');
    this.afterImg = this.element.querySelector('.bae-page-after img');
    this.urlText = this.element.querySelector('.landing-browser-url-text');
    this.wordmark = this.element.querySelector('.bae-wordmark');

    this.preloaded = new Set([0]); // slide 0 is already in the HTML

    this.tick = this.#tick.bind(this);
    this.observer = new IntersectionObserver((entries) => (entries[entries.length - 1].isIntersecting ? this.#start() : this.#stop()));
    this.observer.observe(this.element);
  }

  disconnect() {
    this.observer?.disconnect();
    this.#stop();
  }

  #start() {
    this.frame ||= requestAnimationFrame(this.tick);
  }

  #stop() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.anim = null;
  }

  #tick() {
    // Re-read until the animation exists: a modal's first frames can land before its styles resolve.
    this.anim ||= this.card?.getAnimations()[0];
    if (!this.anim) {
      this.frame = requestAnimationFrame(this.tick);
      return;
    }

    const timing = this.anim.effect.getComputedTiming();
    const cycle = timing.currentIteration ?? 0;
    const progress = timing.progress ?? 0;

    this.#applyImage(this.afterImg, this.#slideAt(cycle).after);
    this.#applyImage(this.beforeImg, this.#slideAt(progress >= HANDOFF ? cycle + 1 : cycle).before);
    const labelled = this.#slideAt(progress >= URL_HANDOFF ? cycle + 1 : cycle);
    this.#setText(this.urlText, labelled.url);
    // The toggle reads "Without <wordmark>" from the cycle's first frame, so it has to
    // name the next slide's tool before the boundary — the URL handoff is the last chance.
    this.#setText(this.wordmark, labelled.wordmark);
    this.#preloadNext(cycle);

    this.frame = requestAnimationFrame(this.tick);
  }

  #slideAt(index) {
    const count = this.slides.length;
    return this.slides[((index % count) + count) % count];
  }

  // Warm the slide one cycle ahead so its swap hits cache. Runs every frame but
  // fetches each slide at most once; the visible <img>'s `sizes` is copied so the
  // browser picks the same width variant the swap will request.
  #preloadNext(cycle) {
    const index = (cycle + 1) % this.slides.length;
    if (this.preloaded.has(index)) return;
    this.preloaded.add(index);

    const sizes = this.afterImg?.sizes || '';
    const slide = this.slides[index];
    for (const data of [slide.before, slide.after]) {
      const img = new Image();
      if (sizes) img.sizes = sizes;
      if (data.srcset) img.srcset = data.srcset;
      img.src = data.src;
    }
  }

  // alt sits outside the src guard: SiteBlocker's slides share one block-page capture, never its label.
  #applyImage(img, data) {
    if (!img || !data) return;

    if (img.alt !== data.alt) img.alt = data.alt;
    if (img.getAttribute('src') === data.src) return;

    img.srcset = data.srcset;
    img.src = data.src;
  }

  #setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }
}
